import tailwindcss from "@headlessui/tailwindcss";
import forms from "@tailwindcss/forms";
import typography from "@tailwindcss/typography";
import type { Config } from "tailwindcss";
import animate from "tailwindcss-animate";

export default {
  darkMode: "class",
  // Change this with the extensions you are going to use.
  content: ["./src/**/*.{html,js,jsx,md,mdx,svelte,ts,tsx,vue,svg}"],
  theme: {
    screens: {
      // These are the default media queries.
      // We're declaring them to make it easier to import and use in react for js checks
      sm: "640px",
      md: "768px",
      lg: "1024px",
      xl: "1280px",
      "2xl": "1536px",
    },
    extend: {
      keyframes: {
        "accordion-down": {
          from: { height: "0" },
          to: { height: "var(--radix-accordion-content-height)" },
        },
        "accordion-up": {
          from: { height: "var(--radix-accordion-content-height)" },
          to: { height: "0" },
        },
      },
      animation: {
        "accordion-down": "accordion-down 0.2s ease-out",
        "accordion-up": "accordion-up 0.2s ease-out",
      },
      fontFamily: {
        geist: ["Geist"],
      },
      maxHeight: {
        "combobox-values": "210px",
      },
      height: {
        "combobox-values": "210px",
        "inviza-logo-lg": "180px",
        "inviza-logo-sm": "100px",
      },
      maxWidth: {
        toast: "320px",
        form: "376px",
      },
      borderWidth: {
        3: "3px",
      },
      width: {
        "modal-sm": "433px",
        "modal-lg": "611px",
        "inviza-logo-lg": "180px",
        "inviza-logo-sm": "100px",
      },
      colors: {
        background: {
          primary: "#FFFFFF",
          "primary-dark": "#111111",
        },
        "surface-container": {
          high: "#F3F4F6",
          "high-dark": "#22262d",
          low: "#F9FAFB",
          "low-dark": "#0D1117",
          highest: "#E5E7EB",
          "highest-dark": "#383C43",
          DEFAULT: "#E5E7EB",
          "default-dark": "#181C22",
        },
        primary: {
          10: "#001C3A",
          20: "#00315E",
          30: "#004784",
          40: "#005FAD",
          50: "#2878CD",
          60: "#4A93E9",
          70: "#70AEFF",
          80: "#A4C8FF",
          90: "#D4E3FF",
          95: "#EBF1FF",
          98: "#F9F9FF",
          DEFAULT: "#F0F8FF",
        },
        secondary: {
          10: "#0F1C2C",
          20: "#253142",
          30: "#3B4859",
          40: "#525F72",
          50: "#6B788B",
          60: "#8592A6",
          70: "#9FACC1",
          80: "#BAC7DD",
          90: "#D6E3FA",
          95: "#EAF1FF",
          98: "#F8F9FF",
          DEFAULT: "#EEF0FA",
        },
        tertiary: {
          10: "#001E31",
          20: "#003351",
          30: "#004B73",
          40: "#126394",
          50: "#377CAE",
          60: "#5596CA",
          70: "#71B1E6",
          80: "#92CCFF",
          90: "#CCE5FF",
          95: "#E7F2FF",
          98: "#F7F9FF",
          DEFAULT: "#C3C6CF",
        },
        neutral: {
          10: "#1C1B1C",
          20: "#313030",
          15: "#22262D",
          30: "#474647",
          40: "#5F5E5E",
          50: "#787777",
          60: "#929090",
          70: "#ADABAA",
          80: "#C9C6C6",
          90: "#E5E2E1",
          95: "#F3F0F0",
          98: "#FCF8F8",
        },
        "neutral-variant": {
          10: "#181C22",
          15: "#22262D",
          20: "#2D3138",
          30: "#43474E",
          35: "#4F525A",
          40: "#5B5E66",
          50: "#74777F",
          60: "#8D9199",
          70: "#A8ABB4",
          80: "#C3C6CF",
          90: "#E0E2EB",
          95: "#EEF0FA",
          98: "#F9F9FF",
        },
        error: {
          10: "#410004",
          20: "#68000C",
          30: "#930015",
          40: "#BB1725",
          50: "#DE353A",
          60: "#FF5353",
          70: "#FF8983",
          80: "#FFB3AE",
          90: "#FFDAD7",
          95: "#FFEDEB",
          98: "#FFF8F7",
        },
        success: {
          10: "#032100",
          20: "#073900",
          30: "#0E5300",
          40: "#166E00",
          50: "#1E8A00",
          60: "#26A700",
          70: "#2FC500",
          80: "#4FE32B",
          90: "#BDF1A8",
          95: "#CBFFB5",
          98: "#EDFFE0",
        },
        info: {
          10: "#001D34",
          20: "#003355",
          30: "#004A79",
          40: "#00629E",
          50: "#007CC6",
          60: "#0096EF",
          70: "#59B1FF",
          80: "#9ACBFF",
          90: "#CFE5FF",
          95: "#E9F1FF",
          98: "#F7F9FF",
        },
        warning: {
          10: "#251A00",
          20: "#3E2E00",
          30: "#5A4300",
          40: "#775A00",
          50: "#957200",
          60: "#B58B00",
          70: "#D5A400",
          80: "#F7BE00",
          90: "#FFDF97",
          95: "#FFEFD1",
          98: "#FFF8F1",
        },
        complementary: {
          orange: "#E6A437",
          green: "#85CF28",
          violet: "#BF59F2",
          "light-blue": "#6ECBFF",
          red: "#FF375E",
          cyan: "#63E6E2",
          disabled: "#302E34",
        },
      },
    },
  },
  plugins: [typography, forms, tailwindcss, animate],
} satisfies Config;
